/**
 * ScrollView que na web permite scroll do documento (conteúdo passa atrás da barra do Safari).
 * O background é garantido via html/body/#root com min-height no global.css.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import { runOnJS, useAnimatedScrollHandler } from 'react-native-reanimated';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BAR_HEIGHT } from '@/components/keyboard/keyboard-bar';

/**
 * Folga entre o campo focado e o topo do teclado: cabe a nota/erro abaixo do
 * campo e, no iOS, a barra anterior/próximo/concluir.
 */
export const KEYBOARD_BOTTOM_OFFSET = 32 + KEYBOARD_BAR_HEIGHT;

type DashboardScrollViewProps = React.ComponentProps<typeof ScrollView> & {
  contentContainerStyle?: React.ComponentProps<typeof ScrollView>['contentContainerStyle'];
  /** Chamado a cada scroll com o offset vertical atual (px). Barato: só registra listener se passado. */
  onScrollOffset?: (y: number) => void;
};

export function DashboardScrollView({
  children,
  contentContainerStyle,
  style,
  onScrollOffset,
  onScroll,
  ...props
}: DashboardScrollViewProps) {
  // Refs sempre atualizados no render, lidos de dentro dos handlers — evita
  // re-registrar listener/handler a cada nova arrow function vinda do pai.
  const onScrollOffsetRef = useRef(onScrollOffset);
  onScrollOffsetRef.current = onScrollOffset;
  const onScrollRef = useRef(onScroll);
  onScrollRef.current = onScroll;

  const webNodeRef = useRef<View>(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const node = webNodeRef.current as unknown as HTMLElement | null;
    if (!node || !onScrollOffsetRef.current) return;
    const handleScroll = () => onScrollOffsetRef.current?.(node.scrollTop);
    node.addEventListener('scroll', handleScroll, { passive: true });
    return () => node.removeEventListener('scroll', handleScroll);
    // Deps estáveis de propósito: registra uma vez; handleScroll sempre lê o ref atual.
  }, []);

  // Funções estáveis (identidade fixa) para runOnJS não recriar o handler nativo à toa.
  const emitScrollOffset = useCallback((y: number) => {
    onScrollOffsetRef.current?.(y);
  }, []);
  const emitRawScroll = useCallback((nativeEvent: NativeScrollEvent) => {
    onScrollRef.current?.({ nativeEvent } as NativeSyntheticEvent<NativeScrollEvent>);
  }, []);

  const scrollHandler = useAnimatedScrollHandler((event) => {
    // Ambos podem coexistir: onScrollOffset (novo contrato) e onScroll espalhado (legado) rodam os dois.
    if (onScrollOffsetRef.current) {
      runOnJS(emitScrollOffset)(event.contentOffset.y);
    }
    if (onScrollRef.current) {
      runOnJS(emitRawScroll)(event);
    }
  });

  if (Platform.OS === 'web') {
    return (
      <View
        ref={webNodeRef}
        // No layout com sidebar (desktop), o body não deve scrollar; então o conteúdo
        // precisa ser scrollável aqui dentro.
        style={[{ width: '100%', flex: 1, minHeight: 0, overflowY: 'auto' } as any, style]}
        {...(props as any)}
      >
        <View style={[{ width: '100%' }, contentContainerStyle as object]}>{children}</View>
      </View>
    );
  }
  // No celular, o campo focado sobe sozinho até ficar acima do teclado, e um
  // toque em botão/opção com o teclado aberto já age (não gasta o toque
  // fechando o teclado).
  return (
    <KeyboardAwareScrollView
      contentContainerStyle={contentContainerStyle}
      style={style}
      onScroll={scrollHandler}
      bottomOffset={KEYBOARD_BOTTOM_OFFSET}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="interactive"
      {...props}
    >
      {children}
    </KeyboardAwareScrollView>
  );
}
